import { useEffect, useState } from 'react';
import {
  fetchLecture,
  fetchLectures,
  fetchTerms,
  generateLectureNote,
  renameLecture,
  searchLectures,
  type LectureMeta,
  type LectureSearchResult,
  type LectureSegment,
} from '../api.js';
import { getDeviceIdentity } from '../device.js';
import { downloadFile } from '../export/formats.js';
import { buildTermMatcher, TermText, type TermMatcher } from '../terms/highlight.js';
import { TermPopup } from '../terms/TermPopup.js';

/**
 * 講義メモ画面。
 *
 * 過去の講義がサーバーに残っており、後から読み返し・タイトル整理・
 * AIによる講義ノート化・Markdown ダウンロードができる。
 * 「表示して終わり」にしないための画面。
 */
export function Lectures({ onBack }: { onBack: () => void }): JSX.Element {
  const [lectures, setLectures] = useState<LectureMeta[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [error, setError] = useState<string>();
  // 既定は「自分」の講義だけ表示(現地要望: 2人のメモが混ざって取り違えが起きた)。
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [myDeviceId, setMyDeviceId] = useState('');
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<LectureSearchResult[]>();
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    // 端末IDを添えて取得する。開発者のテスト記録は本人以外の一覧に出ない(サーバー側判定)。
    void getDeviceIdentity().then((identity) => {
      setMyDeviceId(identity.deviceId);
      fetchLectures(identity.deviceId)
        .then((data) => setLectures(data.lectures))
        .catch((e: unknown) =>
          setError(e instanceof Error ? e.message : '講義一覧を取得できませんでした。'),
        );
    });
  }, []);

  const runSearch = async (): Promise<void> => {
    const q = query.trim();
    if (q === '') {
      setSearchResults(undefined);
      return;
    }
    setSearching(true);
    try {
      const data = await searchLectures(q, myDeviceId);
      setSearchResults(data.results);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '検索に失敗しました。');
    } finally {
      setSearching(false);
    }
  };

  // スマホの戻る操作で「詳細 → 一覧」へ戻れるようにする。
  // 詳細を開くとき history に積み、pop で state が detail でなくなったら一覧へ。
  useEffect(() => {
    const handlePop = (event: PopStateEvent): void => {
      const screen = (event.state as { screen?: string } | null)?.screen;
      if (screen !== 'lecture-detail') setSelectedId(undefined);
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  const openLecture = (id: string): void => {
    window.history.pushState({ screen: 'lecture-detail' }, '');
    setSelectedId(id);
  };

  // 一覧と同じ並び(スコープ適用済み・更新日時降順)。前後移動もこの並びに従う。
  const visible = lectures.filter(
    (l) => scope === 'all' || !myDeviceId || l.ownerDeviceId === myDeviceId,
  );

  if (selectedId) {
    const index = visible.findIndex((l) => l.lectureId === selectedId);
    // 一覧は新しい順なので、時系列の「前の講義」= index+1、「次の講義」= index-1。
    const prev = index >= 0 ? visible[index + 1] : undefined;
    const next = index >= 1 ? visible[index - 1] : undefined;
    // 「一覧へ」ボタンも history.back() に統一(スワイプと同じ経路)。
    return (
      <LectureDetail
        id={selectedId}
        prev={prev}
        next={next}
        onNavigate={setSelectedId}
        onBack={() => window.history.back()}
        onStartTranslation={() => {
          // 履歴は 設定 → 一覧 → 詳細 の2段深い位置にいるので、2つ戻ると設定画面。
          // popstate 経由で App が画面を切り替える(スワイプ戻る2回と同じ経路)。
          window.history.go(-2);
        }}
      />
    );
  }

  return (
    <div className="screen">
      <div className="row">
        <button onClick={onBack}>← 戻る</button>
        <h1 className="app-title" style={{ margin: 0 }}>
          講義メモ
        </h1>
      </div>
      <p className="app-subtitle">過去の講義の記録。あとから読み返し・資料化ができます。</p>

      {error && <div className="notice error">{error}</div>}

      <div className="row" style={{ marginBottom: 10 }}>
        <button className={scope === 'mine' ? 'primary' : ''} onClick={() => setScope('mine')}>
          自分のメモ
        </button>
        <button className={scope === 'all' ? 'primary' : ''} onClick={() => setScope('all')}>
          全員のメモ
        </button>
      </div>

      <form
        className="row"
        style={{ marginBottom: 14 }}
        onSubmit={(e) => {
          e.preventDefault();
          void runSearch();
        }}
      >
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (e.target.value.trim() === '') setSearchResults(undefined);
          }}
          placeholder="講義メモを検索(日本語・中国語)"
          style={{ flex: 1 }}
        />
        <button type="submit" disabled={searching} style={{ flex: 'none' }}>
          {searching ? '検索中…' : '検索'}
        </button>
      </form>

      {searchResults !== undefined ? (
        searchResults.length === 0 ? (
          <div className="empty">「{query}」に一致する講義メモはありませんでした。</div>
        ) : (
          <div className="profile-list">
            {searchResults.map(({ meta, matches, noteMatches }) => (
              <button
                key={meta.lectureId}
                className="profile-item"
                style={{ textAlign: 'left', display: 'block' }}
                onClick={() => openLecture(meta.lectureId)}
              >
                <strong>{meta.title}</strong>
                {meta.ownerName && <span className="hint">(記録: {meta.ownerName})</span>}
                {matches.map((m) => (
                  <span key={m.segmentId} className="hint" style={{ display: 'block', marginTop: 4 }}>
                    {m.translatedJa.slice(0, 60)}
                  </span>
                ))}
                {(noteMatches ?? []).map((snippet, i) => (
                  <span key={`note-${i}`} className="hint" style={{ display: 'block', marginTop: 4 }}>
                    📝 ノート: {snippet}
                  </span>
                ))}
              </button>
            ))}
          </div>
        )
      ) : lectures.length === 0 && !error ? (
        <div className="empty">
          まだ講義の記録がありません。
          <br />
          翻訳を開始すると、確定した字幕が自動でここへ保存されます。
        </div>
      ) : (
        <div className="profile-list">
          {lectures
            .filter((l) => scope === 'all' || !myDeviceId || l.ownerDeviceId === myDeviceId)
            .map((lecture) => (
            <button
              key={lecture.lectureId}
              className="profile-item"
              style={{ textAlign: 'left', display: 'flex' }}
              onClick={() => openLecture(lecture.lectureId)}
            >
              <span style={{ flex: 1 }}>
                {lecture.title}
                {lecture.hasNote && ' 📝'}
                <span className="hint" style={{ display: 'block' }}>
                  {/* 開始〜終了の両方を表示する。開始だけ・終了だけだと
                      「あの時刻の講義が無い」という取り違えが起きる(現地報告)。 */}
                  {formatRange(lecture.startedAt, lecture.updatedAt)}・{lecture.segmentCount} 発話
                  {scope === 'all' && `・記録: ${lecture.ownerName || '名前未設定'}`}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LectureDetail({
  id,
  prev,
  next,
  onNavigate,
  onBack,
  onStartTranslation,
}: {
  id: string;
  /** 時系列でひとつ前(古い)の講義。無ければボタンを出さない。 */
  prev?: LectureMeta;
  /** 時系列でひとつ後(新しい)の講義。 */
  next?: LectureMeta;
  onNavigate: (id: string) => void;
  onBack: () => void;
  /** 翻訳開始画面へ直接戻る(現地要望: ノートを見ていてすぐ録音を始めたい)。 */
  onStartTranslation: () => void;
}): JSX.Element {
  const [meta, setMeta] = useState<LectureMeta>();
  const [segments, setSegments] = useState<LectureSegment[]>([]);
  // 用語ハイライト(字幕画面と同じ)。失敗しても表示には影響させない。
  const [matcher, setMatcher] = useState<TermMatcher>({ regex: undefined, terms: {} });
  const [termPopup, setTermPopup] = useState<{ ja: string; zh: string } | undefined>();
  useEffect(() => {
    fetchTerms()
      .then((data) => setMatcher(buildTermMatcher(data.terms)))
      .catch(() => undefined);
  }, []);
  const [note, setNote] = useState<string>();
  const [view, setView] = useState<'transcript' | 'note'>('transcript');
  const [error, setError] = useState<string>();
  const [generating, setGenerating] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');

  const load = (): void => {
    fetchLecture(id)
      .then((data) => {
        setMeta(data.meta);
        setSegments(data.segments);
        setNote(data.note);
        setTitleDraft(data.meta.title);
        // 前後移動でノートの無い講義に来たら全文表示に切り替える。
        // (逆にノートがあれば、直前までノートを見ていた人はそのままノートが見える)
        if (!data.note) setView('transcript');
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : '講義を取得できませんでした。'),
      );
  };

  useEffect(load, [id]);

  const handleGenerate = async (): Promise<void> => {
    setGenerating(true);
    setError(undefined);
    try {
      const result = await generateLectureNote(id);
      setNote(result.note);
      setView('note');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'ノートを生成できませんでした。');
    } finally {
      setGenerating(false);
    }
  };

  const handleRename = async (): Promise<void> => {
    const title = titleDraft.trim();
    if (title === '' || !meta) return;
    try {
      await renameLecture(id, title);
      setMeta({ ...meta, title });
      setEditingTitle(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'タイトルを変更できませんでした。');
    }
  };

  const downloadNote = (): void => {
    if (note) downloadFile(`${id}-note.md`, note, 'text/markdown');
  };

  const downloadTranscript = (): void => {
    const text = segments.map((s) => `${s.sourceZh}\n${s.translatedJa}\n`).join('\n');
    downloadFile(`${id}-transcript.txt`, text, 'text/plain');
  };

  return (
    <div className="screen">
      <div className="row" style={{ alignItems: 'center', flexWrap: 'nowrap', gap: 6 }}>
        <button onClick={onBack}>一覧へ</button>
        <button className="primary" onClick={onStartTranslation}>
          🎤 翻訳へ
        </button>
        <div className="spacer" />
        <button disabled={!prev} onClick={() => prev && onNavigate(prev.lectureId)}>
          ← 前
        </button>
        <button disabled={!next} onClick={() => next && onNavigate(next.lectureId)}>
          次 →
        </button>
      </div>

      {termPopup && (
        <TermPopup ja={termPopup.ja} zh={termPopup.zh} onClose={() => setTermPopup(undefined)} />
      )}

      {error && <div className="notice error">{error}</div>}

      {meta && (
        <>
          {editingTitle ? (
            <div className="row" style={{ margin: '12px 0' }}>
              <input value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} />
              <button className="primary" onClick={() => void handleRename()}>
                保存
              </button>
            </div>
          ) : (
            <h1 className="app-title">
              {meta.title}{' '}
              <button
                style={{ fontSize: 12, minHeight: 32, padding: '4px 10px' }}
                onClick={() => setEditingTitle(true)}
              >
                名前を変更
              </button>
            </h1>
          )}
          <p className="app-subtitle">
            {formatRange(meta.startedAt, meta.updatedAt)}・{meta.segmentCount} 発話
          </p>

          <div className="row" style={{ marginBottom: 14 }}>
            <button
              className={view === 'transcript' ? 'primary' : ''}
              onClick={() => setView('transcript')}
            >
              全文
            </button>
            <button
              className={view === 'note' ? 'primary' : ''}
              onClick={() => setView('note')}
              disabled={!note}
            >
              講義ノート
            </button>
            <div className="spacer" />
            <button onClick={() => void handleGenerate()} disabled={generating}>
              {generating ? '生成中…(数十秒かかります)' : note ? 'ノートを作り直す' : 'ノートを作成'}
            </button>
          </div>

          {view === 'transcript' ? (
            <>
              <div className="row" style={{ marginBottom: 10 }}>
                <button onClick={downloadTranscript}>全文を TXT で保存</button>
              </div>
              {segments.map((s) => (
                <div key={s.segmentId} className="segment">
                  {s.kind === 'photo' && s.photo && (
                    <img
                      src={`/api/lectures/${encodeURIComponent(id)}/photos/${encodeURIComponent(s.photo)}`}
                      alt="撮影した資料"
                      style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 8, margin: '4px 0' }}
                    />
                  )}
                  <div className="zh" style={{ whiteSpace: 'pre-wrap' }}>
                    {s.sourceZh}
                  </div>
                  <div className="ja" style={{ fontSize: 18, whiteSpace: 'pre-wrap' }}>
                    <TermText
                      text={s.translatedJa}
                      matcher={matcher}
                      onTerm={(ja, zh) => setTermPopup({ ja, zh })}
                    />
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              <div className="row" style={{ marginBottom: 10 }}>
                <button onClick={downloadNote} disabled={!note}>
                  Markdown で保存
                </button>
              </div>
              {/* Markdown はそのままテキスト表示する。閲覧目的には十分で、
                  レンダラを入れるとバンドルが太る。ダウンロード後は好きなツールで開ける。 */}
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  fontSize: 15,
                  lineHeight: 1.7,
                  background: 'var(--bg-raised)',
                  padding: 16,
                  borderRadius: 10,
                }}
              >
                {note}
              </pre>
            </>
          )}
        </>
      )}
    </div>
  );
}

/** 「7/30 13:06〜14:16」形式。開始と終了(最終更新)を併記する。 */
function formatRange(startIso: string, endIso: string): string {
  const start = formatDate(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(end.getTime()) || startIso === endIso) return start;
  const endTime = end.toLocaleString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  return `${start}〜${endTime}`;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
